import { useEffect, useMemo, useState } from 'react';
import { business, featuredServices, moreServices } from '../../data/content';

export type EstimateValues = {
  name: string;
  phone: string;
  city: string;
  address: string;
  services: string[];
  details: string;
};

export type EstimateErrors = Partial<Record<keyof EstimateValues, string>>;

export const serviceOptions = [
  ...featuredServices.map((s) => s.title),
  ...moreServices.map((s) => s.label),
  'Other',
];

const empty: EstimateValues = { name: '', phone: '', city: '', address: '', services: [], details: '' };

export function formatPhone(raw: string) {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function validate(v: EstimateValues): EstimateErrors {
  const e: EstimateErrors = {};
  if (v.name.trim().length < 2) e.name = 'Please enter your name.';
  if (v.phone.replace(/\D/g, '').length !== 10) e.phone = 'Enter a 10-digit phone number.';
  if (!v.city) e.city = 'Choose your city.';
  if (v.services.length === 0) e.services = 'Pick at least one service.';
  return e;
}

export type EstimatePhoto = { id: string; file: File; url: string };

export const MAX_PHOTOS = 5;
const MAX_PHOTO_MB = 15;

export function useEstimateForm(open: boolean, initialService?: string) {
  const [values, setValues] = useState<EstimateValues>(empty);
  const [errors, setErrors] = useState<EstimateErrors>({});
  const [step, setStep] = useState<'form' | 'send' | 'done'>('form');
  const [photos, setPhotos] = useState<EstimatePhoto[]>([]);
  const [photoError, setPhotoError] = useState('');
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValues({ ...empty, services: initialService && serviceOptions.includes(initialService) ? [initialService] : [] });
    setErrors({});
    setStep('form');
    setPhotoError('');
    setPhotos((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
  }, [open, initialService]);

  const addPhotos = (list: FileList | null) => {
    if (!list) return;
    setPhotoError('');
    const incoming = Array.from(list);
    const valid = incoming.filter((f) => f.type.startsWith('image/') && f.size <= MAX_PHOTO_MB * 1024 * 1024);
    if (valid.length < incoming.length) setPhotoError(`Only images up to ${MAX_PHOTO_MB}MB are allowed.`);
    setPhotos((prev) => {
      const room = MAX_PHOTOS - prev.length;
      if (valid.length > room) setPhotoError(`You can add up to ${MAX_PHOTOS} photos.`);
      const added = valid.slice(0, Math.max(room, 0)).map((file) => ({
        id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        url: URL.createObjectURL(file),
      }));
      return [...prev, ...added];
    });
  };

  const removePhoto = (id: string) => {
    setPhotoError('');
    setPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.id !== id);
    });
  };

  const canShareFiles = useMemo(() => {
    if (photos.length === 0 || typeof navigator === 'undefined' || !navigator.canShare) return false;
    try {
      return navigator.canShare({ files: photos.map((p) => p.file) });
    } catch {
      return false;
    }
  }, [photos]);

  const setField = <K extends keyof EstimateValues>(key: K, value: EstimateValues[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const toggleService = (s: string) =>
    setField('services', values.services.includes(s) ? values.services.filter((x) => x !== s) : [...values.services, s]);

  const submit = () => {
    const e = validate(values);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep('send');
    return Object.keys(e)[0] as keyof EstimateValues | undefined;
  };

  const message = useMemo(
    () =>
      [
        'New estimate request — Dany Landscaping',
        `Name: ${values.name.trim()}`,
        `Phone: ${values.phone}`,
        `City: ${values.city}`,
        values.address.trim() && `Address: ${values.address.trim()}`,
        `Services: ${values.services.join(', ')}`,
        values.details.trim() && `Details: ${values.details.trim()}`,
        photos.length > 0 && `Photos: ${photos.length} attached`,
      ]
        .filter(Boolean)
        .join('\n'),
    [values, photos.length]
  );

  const smsHref = `sms:${business.smsNumber}?&body=${encodeURIComponent(message)}`;
  const whatsappHref = `https://wa.me/${business.whatsappNumber}?text=${encodeURIComponent(message)}`;

  const shareWithPhotos = async () => {
    if (!canShareFiles) return;
    setSharing(true);
    try {
      await navigator.share({
        title: 'Estimate request — Dany Landscaping',
        text: `${message}\n\nSend to: ${business.phoneDisplay}`,
        files: photos.map((p) => p.file),
      });
      setStep('done');
    } catch {
      // User cancelled the share sheet — stay on this step.
    } finally {
      setSharing(false);
    }
  };

  return {
    values,
    errors,
    step,
    setStep,
    setField,
    toggleService,
    submit,
    message,
    smsHref,
    whatsappHref,
    photos,
    photoError,
    addPhotos,
    removePhoto,
    canShareFiles,
    shareWithPhotos,
    sharing,
  };
}
